/* eslint-disable prettier/prettier */
/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import IsCidr from 'is-cidr';
import React from 'react';
import { decorate, observable, computed, action, runInAction, reaction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon, Popup } from 'semantic-ui-react';

import Stores from '@amzn/base-ui/dist/models/Stores';
import { displayError, displaySuccess } from '@amzn/base-ui/dist/helpers/notification';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import { createCidrFormModel } from '../../../models/forms/CreateCidrFormModel';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// Placeholder IP used when a rule has no IPs (hidden from user)
const PLACEHOLDER_IP = '127.0.0.1/32';

// Common CIDR suffix sizes for autocomplete (smallest first)
const CIDR_SUFFIXES = [32, 28, 24, 20, 16];

/**
 * Generate CIDR suggestions based on partial IP input.
 * Returns an array of valid CIDR strings that start with the input.
 */
function getCidrSuggestions(input) {
  if (!input || !input.trim()) return [];
  const val = input.trim();

  // If already a valid CIDR, no suggestions needed
  if (IsCidr(val)) return [];

  // If input already has a slash, try completing the suffix
  if (val.includes('/')) {
    const [ipPart, prefixPart] = val.split('/');
    if (!ipPart) return [];
    // Pad IP if needed
    const paddedIp = padIp(ipPart);
    if (!paddedIp) return [];
    return CIDR_SUFFIXES
      .filter(s => String(s).startsWith(prefixPart) && s >= 16)
      .map(s => `${paddedIp}/${s}`)
      .filter(c => IsCidr(c));
  }

  // No slash yet — pad the IP and suggest all suffix sizes
  const paddedIp = padIp(val);
  if (!paddedIp) return [];
  return CIDR_SUFFIXES
    .map(s => `${paddedIp}/${s}`)
    .filter(c => IsCidr(c));
}

/** Pad a partial IP like "131" → "131.0.0.0", "10.0" → "10.0.0.0" */
function padIp(partial) {
  // Only allow digits and dots
  if (!/^[\d.]+$/.test(partial)) return null;
  const parts = partial.split('.');
  if (parts.length > 4) return null;
  // Validate each octet so far
  for (const p of parts) {
    if (p === '') continue; // trailing dot is ok
    const num = parseInt(p, 10);
    if (isNaN(num) || num < 0 || num > 255) return null;
  }
  // Pad to 4 octets
  while (parts.length < 4) parts.push('0');
  // Ensure each part is a valid number
  const filled = parts.map(p => (p === '' ? '0' : p));
  return filled.join('.');
}

// Protocol info for user-friendly display
const protocolInfo = {
  '22': { name: 'SSH', icon: 'terminal', description: 'Secure Shell - Command line access' },
  '80': { name: 'HTTP', icon: 'globe', description: 'Web traffic (unencrypted)' },
  '443': { name: 'HTTPS', icon: 'lock', description: 'Secure web traffic' },
  '3389': { name: 'RDP', icon: 'desktop', description: 'Remote Desktop - Windows access' },
  '8443': { name: 'DCV', icon: 'tv', description: 'NICE DCV - High-performance remote display' },
};

// expected props
// - environment (via prop)
// - scEnvironmentsStore  (via injection)
// - clientInformationStore (via injection)
// - onCancel is a function to pass back
class ScEnvironmentUpdateCidrs extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.stores = new Stores([this.getEnvStore(), this.clientInformationStore, this.envsStore]);
      this.newCidrInput = '';
      this.cidrSuggestions = [];
      this.activeSuggestionRule = null;
      this.validateMaxCidrs = false;
      this.processing = false;
      // Track which rules originally had the placeholder IP (to avoid unnecessary updates)
      this.originallyHadPlaceholder = [];
    });
    const sideEffect = storeReady => {
      runInAction(() => {
        if (storeReady) {
          this.ingressRules = _.map(this.environment.cidr, cidr => ({
            protocol: cidr.protocol,
            fromPort: cidr.fromPort,
            toPort: cidr.toPort,
            cidrBlocks: [],
          }));
          this.form = createCidrFormModel({
            existingValues: this.ingressRules,
          });
          // Track which rules originally had placeholder IP
          this.originallyHadPlaceholder = [];
          
          _.forEach(this.ingressRules, (ingressRule, idx) => {
            const origRuleIndex = _.findIndex(this.environment.cidr, {
              protocol: ingressRule.protocol,
              fromPort: ingressRule.fromPort,
              toPort: ingressRule.toPort,
            });

            let cidrBlocks = [...this.environment.cidr[origRuleIndex].cidrBlocks];
            
            // Check if this rule originally had the placeholder IP
            const hadPlaceholder = _.includes(cidrBlocks, PLACEHOLDER_IP);
            this.originallyHadPlaceholder[origRuleIndex] = hadPlaceholder;
            
            // Remove placeholder IP from display (user shouldn't see it)
            cidrBlocks = cidrBlocks.filter(cidr => cidr !== PLACEHOLDER_IP);
            
            this.ingressRules[origRuleIndex].cidrBlocks = cidrBlocks;
            this.form.$(`cidr[${origRuleIndex}].cidrBlocks`).set(cidrBlocks);
          });
        }
      });
    };
    this.disposer = reaction(() => {
      return this.stores.ready;
    }, sideEffect);
    sideEffect(this.stores.ready);
  }

  componentDidMount() {
    swallowError(this.getStores().load());
  }

  componentWillUnmount() {
    if (this.disposer) {
      this.disposer();
    }

    const store = this.getEnvStore();
    if (store) {
      store.stopHeartbeat();
    }
  }

  get environment() {
    return this.props.scEnvironment;
  }

  get envsStore() {
    return this.props.scEnvironmentsStore;
  }

  get clientInformationStore() {
    return this.props.clientInformationStore;
  }

  getEnvStore() {
    const instanceId = this.environment.id;
    const store = this.envsStore;
    return store.getScEnvironmentStore(instanceId);
  }

  handleCancel = () => {
    if (this.form) this.form.reset();
    this.props.onCancel();
  };

  getStores() {
    return this.stores;
  }

  handleSubmit = async () => {
    const store = this.envsStore;
    runInAction(() => { this.processing = true; });
    try {
      const values = this.form.values();
      
      // Add placeholder IP to empty rules before submitting
      const processedValues = {
        cidr: values.cidr.map((rule, index) => {
          let cidrBlocks = [...rule.cidrBlocks];
          
          // If rule has no IPs, add placeholder
          if (cidrBlocks.length === 0) {
            // Only add if it didn't originally have it (to avoid unnecessary API call)
            // Actually, we always add it if empty - the API needs at least one IP
            cidrBlocks = [PLACEHOLDER_IP];
          }
          
          return {
            ...rule,
            cidrBlocks,
          };
        }),
      };
      
      await store.updateScEnvironmentCidrs(this.environment.id, processedValues);
      displaySuccess('Security settings updated successfully');
      this.props.onCancel();
    } catch (error) {
      displayError(error);
    } finally {
      runInAction(() => { this.processing = false; });
    }
  };

  render() {
    const stores = this.getStores();
    let content = null;
    if (stores.hasError) {
      content = <ErrorBox error={stores.error} className="pt2 mb2" />;
    } else if (stores.loading) {
      content = <ProgressPlaceHolder segmentCount={1} className="mt2 mb2" />;
    } else if (stores.ready) {
      content = this.renderMain();
    } else {
      content = null;
    }

    return <div className="sg-manager-container">{content}</div>;
  }

  handleAddMyIp = ruleIndex => event => {
    event.preventDefault();
    const clientInformationStore = this.clientInformationStore;
    const currentIp = clientInformationStore.ipAddress;
    runInAction(() => {
      if (!_.includes(this.ingressRules[ruleIndex].cidrBlocks, `${currentIp}/32`)) {
        this.ingressRules[ruleIndex].cidrBlocks.push(`${currentIp}/32`);
        this.form.$(`cidr[${ruleIndex}].cidrBlocks`).set(this.ingressRules[ruleIndex].cidrBlocks);
      }
    });
  };

  addMyIpToAll = event => {
    event.preventDefault();
    const clientInformationStore = this.clientInformationStore;
    const currentIp = clientInformationStore.ipAddress;
    runInAction(() => {
      const fields = this.form?.$('cidr');
      if (!fields) return;
      fields.map((item, ruleIndex) => {
        if (!_.includes(this.ingressRules[ruleIndex].cidrBlocks, `${currentIp}/32`)) {
          this.ingressRules[ruleIndex].cidrBlocks.push(`${currentIp}/32`);
          this.form.$(`cidr[${ruleIndex}].cidrBlocks`).set(this.ingressRules[ruleIndex].cidrBlocks);
        }
        return null;
      });
    });
  };

  handleRemoveCidr = (ruleIndex, cidr) => {
    runInAction(() => {
      this.ingressRules[ruleIndex].cidrBlocks = this.ingressRules[ruleIndex].cidrBlocks.filter(c => c !== cidr);
      this.form.$(`cidr[${ruleIndex}].cidrBlocks`).set(this.ingressRules[ruleIndex].cidrBlocks);
    });
  };

  handleAddCidr = (ruleIndex, cidr) => {
    if (!cidr || !cidr.trim()) return;
    const trimmedCidr = cidr.trim();
    runInAction(() => {
      if (!_.includes(this.ingressRules[ruleIndex].cidrBlocks, trimmedCidr)) {
        this.ingressRules[ruleIndex].cidrBlocks.push(trimmedCidr);
        this.form.$(`cidr[${ruleIndex}].cidrBlocks`).set(this.ingressRules[ruleIndex].cidrBlocks);
      }
    });
  };

  handleAddFromInput = (ruleIndex) => {
    const input = document.getElementById(`cidr-input-${ruleIndex}`);
    if (input && input.value) {
      this.handleAddCidr(ruleIndex, input.value);
      input.value = '';
    }
  };

  // Validation helpers
  anyWideCidr = () => {
    return _.some(this.ingressRules, rule =>
      _.some(rule.cidrBlocks, cidr => {
        const parts = cidr.split('/');
        const suffixBits = parts[parts.length - 1];
        return suffixBits <= 16;
      })
    );
  };

  openCidr = () => {
    return _.some(this.ingressRules, rule =>
      _.some(rule.cidrBlocks, cidr => 
        cidr.toString().includes('0.0.0.0') || cidr.toString().split('/')[1] < 16
      )
    );
  };

  onsiteCidr = () => {
    return _.some(this.ingressRules, rule =>
      _.some(rule.cidrBlocks, cidr => cidr.toString().includes('131.170.'))
    );
  };

  anyInvalidCidr = () => {
    return _.some(this.ingressRules, rule =>
      _.some(rule.cidrBlocks, cidr => IsCidr(cidr) !== 4)
    );
  };

  hasEmptyCidrBlocks = () => {
    if (!this.ingressRules) return false;
    return this.ingressRules.some(rule => !rule.cidrBlocks || rule.cidrBlocks.length === 0);
  };

  validateMaxRStudioCidr = () => {
    let status = false;
    const envOutputs = this.environment.outputs;
    const metaConnection1Type = envOutputs.find(obj => obj.OutputKey === 'MetaConnection1Type');
    if (metaConnection1Type) {
      const productName = metaConnection1Type.OutputValue;
      if (productName === 'RStudioV2') {
        status = this.validateMaxCidrs;
      }
    }
    return status;
  };

  getCidrStatus = (cidr) => {
    if (IsCidr(cidr) !== 4) return 'invalid';
    if (cidr.includes('0.0.0.0') || cidr.split('/')[1] < 16) return 'dangerous';
    const suffix = parseInt(cidr.split('/')[1], 10);
    if (suffix <= 16) return 'warning';
    if (cidr.includes('131.170.')) return 'info';
    return 'valid';
  };

  // Check if current IP is added to all rules
  allRulesHaveCurrentIp = () => {
    const currentIp = this.clientInformationStore.ipAddress;
    return this.ingressRules.every(rule => _.includes(rule.cidrBlocks, `${currentIp}/32`));
  };

  renderMain() {
    const form = this.form;
    if (!form) return null;
    
    const fields = form.$('cidr');
    const currentIp = this.clientInformationStore.ipAddress;
    // Only block save for invalid CIDRs, dangerous IPs, or RStudio max - NOT for empty rules
    const hasErrors = this.anyInvalidCidr() || this.validateMaxRStudioCidr() || this.openCidr();
    const allHaveIp = this.allRulesHaveCurrentIp();

    if (!fields.value.length) {
      return (
        <div className="sg-manager-compact sg-manager-empty">
          <Icon name="shield alternate" style={{ ...iconStyle, fontSize: '1.5em', color: '#9ca3af' }} />
          <span>No configurable network access rules for this workspace.</span>
        </div>
      );
    }

    return (
      <div className="sg-manager-compact">
        {/* Compact Header */}
        <div className="sg-compact-header">
          <div className="sg-compact-header-left">
            <Icon name="shield alternate" style={iconStyle} />
            <span className="sg-compact-title">Network Access</span>
          </div>
          <div className="sg-compact-header-right">
            <button 
              className={`sg-btn-compact ${allHaveIp ? 'sg-btn-compact-disabled' : 'sg-btn-compact-outline'}`}
              onClick={this.addMyIpToAll}
              disabled={allHaveIp}
            >
              <Icon name={allHaveIp ? 'check' : 'plus'} style={iconStyle} /> {allHaveIp ? 'Added to All' : 'Add My IP to All'}
            </button>
          </div>
        </div>

        {/* Alerts - compact */}
        {this.renderCompactAlerts()}

        {/* Rules - inline compact layout */}
        <div className="sg-compact-rules">
          {fields.map((field, index) => this.renderCompactRule(field, index))}
        </div>

        {/* Compact Footer */}
        <div className="sg-compact-footer">
          <button className="sg-btn-compact sg-btn-compact-secondary" onClick={this.handleCancel} disabled={this.processing}>
            Cancel
          </button>
          <button className="sg-btn-compact sg-btn-compact-success" onClick={this.handleSubmit} disabled={this.processing || hasErrors}>
            {this.processing ? <><Icon name="spinner" loading style={iconStyle} /> Saving...</> : <><Icon name="check" style={iconStyle} /> Save</>}
          </button>
        </div>
      </div>
    );
  }

  renderCompactRule(field, index) {
    const port = field.value.fromPort;
    const protocol = field.value.protocol;
    const info = protocolInfo[port] || { name: port, icon: 'plug', description: `Port ${port}` };
    const cidrBlocks = this.ingressRules[index].cidrBlocks;
    const currentIp = this.clientInformationStore.ipAddress;
    const hasCurrentIp = _.includes(cidrBlocks, `${currentIp}/32`);
    const hasError = cidrBlocks.length === 0;

    return (
      <div key={index} className={`sg-compact-rule-inline ${hasError ? 'sg-compact-rule-error' : ''}`}>
        {/* Left: Protocol info */}
        <div className="sg-inline-protocol">
          <div className="sg-compact-rule-icon-sm">
            <Icon name={info.icon} style={iconStyle} />
          </div>
          <div className="sg-inline-protocol-text">
            <span className="sg-compact-rule-name">{info.name}</span>
            <span className="sg-compact-rule-desc-sm">{info.description}</span>
          </div>
        </div>

        {/* Middle: CIDR tags and input */}
        <div className="sg-inline-cidrs">
          {cidrBlocks.map((cidr) => this.renderCompactCidrTag(cidr, index))}
          {cidrBlocks.length === 0 && <span className="sg-compact-empty-inline">No IPs</span>}
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <input
              type="text"
              className="sg-compact-input-inline"
              placeholder="Add another IP..."
              id={`cidr-input-${index}`}
              autoComplete="off"
              onInput={(e) => {
                const val = e.target.value;
                const btn = document.getElementById(`cidr-add-btn-${index}`);
                if (btn) btn.disabled = !IsCidr(val.trim());
                runInAction(() => {
                  this.cidrSuggestions = getCidrSuggestions(val);
                  this.activeSuggestionRule = this.cidrSuggestions.length > 0 ? index : null;
                });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value && IsCidr(e.target.value.trim())) {
                  this.handleAddCidr(index, e.target.value);
                  e.target.value = '';
                  const btn = document.getElementById(`cidr-add-btn-${index}`);
                  if (btn) btn.disabled = true;
                  runInAction(() => { this.cidrSuggestions = []; this.activeSuggestionRule = null; });
                }
                if (e.key === 'Escape') {
                  runInAction(() => { this.cidrSuggestions = []; this.activeSuggestionRule = null; });
                }
              }}
              onBlur={() => {
                // Delay to allow click on suggestion
                setTimeout(() => runInAction(() => { this.cidrSuggestions = []; this.activeSuggestionRule = null; }), 150);
              }}
            />
            {this.activeSuggestionRule === index && this.cidrSuggestions.length > 0 && (
              <div className="sg-cidr-suggestions">
                {this.cidrSuggestions.map(suggestion => (
                  <button
                    key={suggestion}
                    className="sg-cidr-suggestion-item"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const input = document.getElementById(`cidr-input-${index}`);
                      if (input) input.value = suggestion;
                      const btn = document.getElementById(`cidr-add-btn-${index}`);
                      if (btn) btn.disabled = !IsCidr(suggestion);
                      this.handleAddCidr(index, suggestion);
                      if (input) input.value = '';
                      if (btn) btn.disabled = true;
                      runInAction(() => { this.cidrSuggestions = []; this.activeSuggestionRule = null; });
                    }}
                  >
                    <code>{suggestion}</code>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button 
            className="sg-btn-compact sg-btn-compact-xs sg-btn-compact-outline"
            id={`cidr-add-btn-${index}`}
            onClick={() => {
              this.handleAddFromInput(index);
              const btn = document.getElementById(`cidr-add-btn-${index}`);
              if (btn) btn.disabled = true;
              runInAction(() => { this.cidrSuggestions = []; this.activeSuggestionRule = null; });
            }}
            title="Add IP"
            disabled
          >
            Add
          </button>
        </div>

        {/* Right: Add My IP button */}
        <button 
          className={`sg-btn-compact sg-btn-compact-sm ${hasCurrentIp ? 'sg-btn-compact-disabled' : 'sg-btn-compact-outline'}`}
          onClick={this.handleAddMyIp(index)}
          disabled={hasCurrentIp}
          title={hasCurrentIp ? 'Already added' : `Add ${currentIp}/32`}
        >
          <Icon name={hasCurrentIp ? 'check' : 'plus'} style={iconStyle} /> {hasCurrentIp ? 'Added' : 'Add My IP'}
        </button>
      </div>
    );
  }

  renderCompactCidrTag(cidr, ruleIndex) {
    const status = this.getCidrStatus(cidr);
    const currentIp = this.clientInformationStore.ipAddress;
    const isCurrentIp = cidr === `${currentIp}/32`;

    const statusClass = {
      valid: 'sg-tag-valid',
      invalid: 'sg-tag-invalid',
      dangerous: 'sg-tag-dangerous',
      warning: 'sg-tag-warning',
      info: 'sg-tag-info',
    }[status];

    return (
      <span key={cidr} className={`sg-compact-tag ${statusClass}`}>
        {cidr}{isCurrentIp && ' (you)'}
        <button className="sg-compact-tag-remove" onClick={() => this.handleRemoveCidr(ruleIndex, cidr)}>×</button>
      </span>
    );
  }

  renderCompactAlerts() {
    const alerts = [];

    if (this.openCidr()) {
      alerts.push(<span key="open" className="sg-compact-alert sg-compact-alert-error"><Icon name="exclamation triangle" style={iconStyle} /> Dangerous IP range detected</span>);
    }
    if (this.anyInvalidCidr()) {
      alerts.push(<span key="invalid" className="sg-compact-alert sg-compact-alert-error"><Icon name="times circle" style={iconStyle} /> Invalid IP format</span>);
    }
    if (this.validateMaxRStudioCidr()) {
      alerts.push(<span key="rstudio" className="sg-compact-alert sg-compact-alert-error"><Icon name="times circle" style={iconStyle} /> RStudio max 4 IPs on port 443</span>);
    }
    if (this.hasEmptyCidrBlocks()) {
      alerts.push(<span key="empty" className="sg-compact-alert sg-compact-alert-warning"><Icon name="warning sign" style={iconStyle} /> Some rules have no IPs</span>);
    }
    if (this.anyWideCidr() && !this.openCidr()) {
      alerts.push(<span key="wide" className="sg-compact-alert sg-compact-alert-warning"><Icon name="warning sign" style={iconStyle} /> Large IP range detected</span>);
    }
    if (this.onsiteCidr()) {
      alerts.push(<span key="onsite" className="sg-compact-alert sg-compact-alert-info"><Icon name="info circle" style={iconStyle} /> Campus IPs are automatically permitted</span>);
    }
    const currentIp = this.clientInformationStore.ipAddress;
    alerts.push(<span key="your-ip" className="sg-compact-alert sg-compact-alert-info"><Icon name="globe" style={iconStyle} /> Your CIDR: <code>{currentIp}/32</code></span>);

    if (alerts.length === 0) return null;
    return <div className="sg-compact-alerts">{alerts}</div>;
  }
}

decorate(ScEnvironmentUpdateCidrs, {
  environment: computed,
  envsStore: computed,
  clientInformationStore: computed,
  ingressRules: observable,
  form: observable,
  processing: observable,
  newCidrInput: observable,
  cidrSuggestions: observable,
  activeSuggestionRule: observable,
  validateMaxCidrs: observable,
  originallyHadPlaceholder: observable,
  handleSubmit: action,
  handleCancel: action,
  handleAddMyIp: action,
  addMyIpToAll: action,
  handleRemoveCidr: action,
  handleAddCidr: action,
  handleAddFromInput: action,
});

export default inject('scEnvironmentsStore', 'clientInformationStore')(withRouter(observer(ScEnvironmentUpdateCidrs)));
